import React from 'react';
import { useIntl } from 'react-intl';
import { IconButton, makeStyles } from '@material-ui/core';
import Badge from '@material-ui/core/Badge';
import Skeleton from '@material-ui/lab/Skeleton';
import { PropTypes } from 'prop-types';
import SettingsIcon from '@material-ui/icons/SettingsSharp';
import { DEFAULT_TARGET_UTILIZATION } from '../../hooks/useTargetUtilizationSettings';

const badgeAnchorOrigin = { vertical: 'top', horizontal: 'right' };

const useBadgeStyles = makeStyles(theme => ({
  dotBadge: {
    '& .MuiBadge-badge': {
      backgroundColor: theme.palette.info.light,
      minWidth: theme.spacing(1.25),
      height: theme.spacing(1.25),
      top: 0,
      right: 0
    }
  },
  numberBadge: {
    '& .MuiBadge-badge': {
      backgroundColor: theme.palette.warning.main,
      color: theme.palette.text.primary,
      minWidth: theme.spacing(2.5),
      height: theme.spacing(2.5),
      padding: theme.spacing(0, 0.75),
      borderRadius: theme.spacing(12.5)
    }
  },
  loadingBadge: {
    '& .MuiBadge-badge': {
      backgroundColor: 'transparent',
      minWidth: theme.spacing(1.25),
      height: theme.spacing(1.25),
      top: 0,
      right: 0,
      padding: 0
    }
  }
}));

const loadingSkeleton = <Skeleton variant="circle" width={10} height={10} />;

const getBadgeProps = ({
  isLoading,
  isDefault,
  targetUtilization,
  classes
}) => {
  if (isLoading) {
    return {
      className: classes.loadingBadge,
      badgeContent: loadingSkeleton
    };
  }

  if (isDefault) {
    return {
      className: classes.dotBadge,
      variant: 'dot'
    };
  }

  return {
    className: classes.numberBadge,
    badgeContent: targetUtilization
  };
};

export const TargetUtilizationSettingsButton = ({
  targetUtilization,
  isLoading,
  onClick
}) => {
  const { formatMessage } = useIntl();
  const classes = useBadgeStyles();
  const isDefault = targetUtilization === DEFAULT_TARGET_UTILIZATION;

  const ariaLabel = formatMessage({
    id: 'resourceManagementDashboard.targetUtilizationSettings.settingsButton'
  });

  return (
    <IconButton onClick={onClick} aria-label={ariaLabel}>
      <Badge
        overlap="circular"
        max={200}
        showZero
        anchorOrigin={badgeAnchorOrigin}
        {...getBadgeProps({ isLoading, isDefault, targetUtilization, classes })}
      >
        <SettingsIcon />
      </Badge>
    </IconButton>
  );
};

TargetUtilizationSettingsButton.propTypes = {
  targetUtilization: PropTypes.number.isRequired,
  isLoading: PropTypes.bool,
  onClick: PropTypes.func.isRequired
};

export default TargetUtilizationSettingsButton;
